import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import HelpHint from './HelpHint';
import {
  sanitizeParamKey, paramsChanged, miscasedKeys, isUnfilledMacro, SLOT_OPTIONS, slotForField,
} from '../campaignParams';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Plus, Trash2, Copy, CheckCircle2, AlertCircle, Beaker, Sparkles, Link2,
  Eye, EyeOff, ArrowDownToLine, Send,
} from 'lucide-react';

// Built-in macros that are always available in postback URLs regardless of
// campaign params. Surfaced in the UI so the buyer doesn't have to guess.
const BUILTIN_MACROS = [
  'click_id', 'sub_id', 'campaign_name', 'source', 'status', 'payout', 'currency',
  'sub_id_1', 'sub_id_2', 'sub_id_3', 'sub_id_4',
  'country', 'email', 'phone', 'gclid', 'fbclid', 'ttclid',
  // Google Ads S2S beacon: {tm} auto-split into account id + conversion label.
  'tm_account', 'tm_label',
];

// Keys are canonical status ids; values are English i18n keys — wrap with
// t() at the render site (t is only available inside the component).
const STATUS_LABELS = {
  lead: 'Lead',
  sale: 'Sale',
  reject: 'Reject',
  upsell: 'Upsell',
  // Same set as the tracker records (W1 types) and the network status map.
  registration: 'Registration',
  deposit: 'Deposit',
  trash: 'Trash',
};

// Incoming postbacks: URLs the operator pastes INTO the affiliate network
// (e.g. TrackBox) so it notifies this campaign when a conversion fires. Each
// network echoes back the click id the operator sent it, under its own macro.
const INBOUND_PRESETS = {
  trackbox: {
    label: 'TrackBox / Tigloo',
    click: '{affclickid}',
    payout: '{payout}',
    hint: "TrackBox: pass your click_id in the offer link's Affclickid parameter so {affclickid} comes back to this campaign. Method GET.",
  },
  generic: {
    label: 'Generic — {click_id}',
    click: '{click_id}',
    payout: '{payout}',
    hint: 'Generic: pass your click_id in the offer link so it comes back to this campaign as {click_id}. Method GET.',
  },
  subid: {
    label: 'Subid — {subid}',
    click: '{subid}',
    payout: '{payout}',
    hint: "Subid: pass your click_id in the offer link's subid parameter so {subid} comes back to this campaign. Method GET.",
  },
};

const INBOUND_EVENTS = [
  { key: 'lead', status: 'lead', withRevenue: false, label: 'Lead' },
  { key: 'ftd', status: 'sale', withRevenue: true, label: 'FTD · Deposit (sale)' },
  { key: 'chargeback', status: 'reject', withRevenue: false, label: 'Chargeback (reject)' },
];

// Build a preview tracking URL showing all declared params as query args so
// the buyer can copy-paste it straight into their ad source. Placeholders with
// no value use the `{key}` form. We DON'T URL-encode the value: the braces of
// ad-network macros (`{campaignid}`, ValueTrack, etc.) must stay literal —
// encoding them to `%7B…%7D` both looks broken and stops Google Ads from
// recognising the macro. Matches the copy-link builder in Campaigns.jsx.
function buildPreviewUrl(campaign, params) {
  const base = `${API}/track/${campaign.tracking_id}`;
  const qs = (params || [])
    // Keys without a default (tm, tml, a…) come from the ad itself, e.g. the
    // Google Ads Final URL suffix: a literal {key} would be sent as is.
    .filter(p => p.key && p.placeholder)
    .map(p => `${p.key}=${p.placeholder}`)
    .join('&');
  return qs ? `${base}?${qs}` : base;
}

export default function CampaignParamsAndPostbacks({ campaign, onUpdated }) {
  const { t } = useT();
  const [params, setParams] = useState(campaign.params || []);
  const [postbacks, setPostbacks] = useState(campaign.postbacks || []);
  const [fireGtag, setFireGtag] = useState(!!campaign.fire_gtag_on_redirect);
  const [ipWhitelist, setIpWhitelist] = useState((campaign.postback_ip_whitelist || []).join(', '));
  const [requireToken, setRequireToken] = useState(campaign.postback_require_token !== false);
  const [events, setEvents] = useState([]);
  const [showEvents, setShowEvents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [inboundPreset, setInboundPreset] = useState('trackbox');
  const [sources, setSources] = useState([]);
  const [tgOpen, setTgOpen] = useState(false);
  const [tgBot, setTgBot] = useState('');
  const [tgChat, setTgChat] = useState('');
  const [tgLead, setTgLead] = useState(true);
  const [tgDep, setTgDep] = useState(true);
  const [gaOpen, setGaOpen] = useState(false);
  const [gaDep, setGaDep] = useState(true);
  const [smLead, setSmLead] = useState(((campaign.postback_status_map || {}).lead || []).join(', '));
  const [smSale, setSmSale] = useState(((campaign.postback_status_map || {}).sale || []).join(', '));
  const [smReject, setSmReject] = useState(((campaign.postback_status_map || {}).reject || []).join(', '));
  const [smIgnore, setSmIgnore] = useState(((campaign.postback_status_map || {}).ignore || []).join(', '));
  const [smRegistration, setSmRegistration] = useState(((campaign.postback_status_map || {}).registration || []).join(', '));
  const [smDeposit, setSmDeposit] = useState(((campaign.postback_status_map || {}).deposit || []).join(', '));
  const [smTrash, setSmTrash] = useState(((campaign.postback_status_map || {}).trash || []).join(', '));

  useEffect(() => {
    setParams(campaign.params || []);
    setPostbacks(campaign.postbacks || []);
    setFireGtag(!!campaign.fire_gtag_on_redirect);
    setIpWhitelist((campaign.postback_ip_whitelist || []).join(', '));
    setSmLead(((campaign.postback_status_map || {}).lead || []).join(', '));
    setSmSale(((campaign.postback_status_map || {}).sale || []).join(', '));
    setSmReject(((campaign.postback_status_map || {}).reject || []).join(', '));
    setSmIgnore(((campaign.postback_status_map || {}).ignore || []).join(', '));
    setSmRegistration(((campaign.postback_status_map || {}).registration || []).join(', '));
    setSmDeposit(((campaign.postback_status_map || {}).deposit || []).join(', '));
    setSmTrash(((campaign.postback_status_map || {}).trash || []).join(', '));
  }, [campaign.id]);

  // Parameter edits live only in this editor until Save; warn before the tab
  // is closed so they are not silently lost.
  const paramsDirty = paramsChanged(campaign.params, params);
  useEffect(() => {
    if (!paramsDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [paramsDirty]);

  useEffect(() => {
    axios.get(API + '/traffic-sources')
      .then((r) => setSources(r.data || []))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const parsedIps = ipWhitelist.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
      const parseList = (s) => s.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
      const statusMap = {};
      if (parseList(smLead).length) statusMap.lead = parseList(smLead);
      if (parseList(smSale).length) statusMap.sale = parseList(smSale);
      if (parseList(smReject).length) statusMap.reject = parseList(smReject);
      if (parseList(smIgnore).length) statusMap.ignore = parseList(smIgnore);
      if (parseList(smRegistration).length) statusMap.registration = parseList(smRegistration);
      if (parseList(smDeposit).length) statusMap.deposit = parseList(smDeposit);
      if (parseList(smTrash).length) statusMap.trash = parseList(smTrash);
      const clean = {
        params: params.filter(p => (p.key || '').trim()),
        postbacks: postbacks.filter(p => (p.url || '').trim()),
        fire_gtag_on_redirect: fireGtag,
        postback_ip_whitelist: parsedIps,
        postback_status_map: statusMap,
        postback_require_token: requireToken,
      };
      const r = await axios.put(`${API}/campaigns/${campaign.id}`, clean);
      toast.success(t('Campaign saved'));
      onUpdated && onUpdated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    } finally {
      setSaving(false);
    }
  };

  const loadEvents = async () => {
    try {
      const r = await axios.get(`${API}/campaigns/${campaign.id}/postback-events`);
      setEvents(r.data || []);
      setShowEvents(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const testOne = async (pb) => {
    try {
      const r = await axios.post(`${API}/campaigns/${campaign.id}/postback-test`, pb);
      if (r.data.ok) {
        toast.success(t('Postback fired') + (r.data.http_status ? ` (HTTP ${r.data.http_status})` : ''));
      } else {
        toast.error(r.data.error || `HTTP ${r.data.http_status}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Test failed'));
    }
  };

  const copyText = (v) => { navigator.clipboard.writeText(v || ''); toast.success(t('Copied')); };
  const inbound = INBOUND_PRESETS[inboundPreset] || INBOUND_PRESETS.generic;
  const buildInboundUrl = (ev) => {
    const qs = [`click_id=${inbound.click}`, `status=${ev.status}`];
    if (ev.withRevenue) qs.push(`revenue=${inbound.payout}`);
    if (campaign.postback_secret) qs.push(`token=${campaign.postback_secret}`);
    return `${API}/postback?${qs.join('&')}`;
  };
  const importFromSource = () => {
    var src = sources.find(function (s) { return s.id === campaign.traffic_source_id; });
    if (!src || !src.parameters) { toast.error(t('This campaign has no source with parameters')); return; }
    var imported = Object.entries(src.parameters).map(function (e) {
      var field = e[0]; var cfg = e[1] || {};
      // Sanitize the key so importing from an older source whose alias still
      // contains spaces (e.g. "Campaign ID") can't produce a %20 in the link
      // or a duplicate of an already-sanitized auto-imported param.
      return {
        name: cfg.label || field,
        key: sanitizeParamKey(cfg.alias || field),
        placeholder: cfg.placeholder || '',
        slot: slotForField(field),
      };
    }).filter(function (p) { return p.key; });
    var existing = new Set(params.map(function (p) { return sanitizeParamKey(p.key); }));
    setParams(params.concat(imported.filter(function (p) { return !existing.has(p.key); })));
    toast.success(t('Macros imported from the source — click Save'));
  };
  const buildTgUrl = (bot, chat, header) => {
    const text = header + '%0ACampaign:%20{campaign_name}%0ASub1:%20{sub_id_1}%0ASub2:%20{sub_id_2}%0ASub3:%20{sub_id_3}%0ASub4:%20{sub_id_4}%0ASource:%20{source}%0APayout:%20{payout}';
    return 'https://api.telegram.org/bot' + bot + '/sendMessage?chat_id=' + chat + '&text=' + text;
  };
  const addTelegram = () => {
    const bot = tgBot.trim();
    const chat = tgChat.trim();
    if (!bot || !chat) { toast.error(t('Enter bot token and chat id')); return; }
    const next = [...postbacks];
    if (tgLead) next.push({ url: buildTgUrl(bot, chat, '✅REG'), method: 'GET', statuses: ['lead'], is_active: true });
    if (tgDep) next.push({ url: buildTgUrl(bot, chat, '❤️DEP'), method: 'GET', statuses: ['sale'], is_active: true });
    setPostbacks(next);
    setTgOpen(false);
    toast.success(t('Telegram postbacks added — press Save to apply'));
  };
  // Google Ads server-side deposit beacon.
  // Leads are sent separately from the hosted landing via client-side gtag after
  // a successful /api/lead response; adding a lead S2S rule here would duplicate
  // that signal. The deposit account id + label come from the per-click {tm} tag.
  // The traffic source's own S2S postback (from its template). XPNSR does not
  // fire it on its own — this copies it into the campaign rules.
  const campaignSource = sources.find((s) => s.id === campaign.traffic_source_id);
  const sourcePostback = (campaignSource && campaignSource.postback_url) || '';
  const addSourcePostback = () => {
    if (postbacks.some((p) => p.url === sourcePostback)) {
      toast.info(t('This source postback is already added'));
      return;
    }
    setPostbacks([...postbacks, { url: sourcePostback, method: 'GET', statuses: ['lead', 'sale'], is_active: true }]);
    toast.success(t('Source postback added — check its placeholders (REPLACE, XXXX…) and press Save'));
  };
  const GA_CONV = 'https://www.googleadservices.com/pagead/conversion';
  const addGoogleAds = () => {
    const next = [...postbacks];
    if (gaDep) next.push({
      url: `${GA_CONV}/{tm_account}/?label={tm_label}&gclid={gclid}&value={payout}&currency={currency}&guid=ON&script=0`,
      method: 'GET', statuses: ['sale'], is_active: true,
    });
    setPostbacks(next);
    // Click-time gtag must remain off: it fires a conversion before a real lead
    // or deposit. Campaign tracking params are not seeded here — declare them
    // once on the traffic source and pull them in with "Import from source".
    setFireGtag(false);
    setGaOpen(false);
    toast.success(t('Google Ads deposit postback added + click-time gtag kept off — press Save to apply'));
  };
  const previewUrl = buildPreviewUrl(campaign, params);
  const badCaseKeys = miscasedKeys(params);
  const knownMacros = [
    ...BUILTIN_MACROS,
    ...params.map(p => p.key).filter(Boolean),
  ];

  return (
    <div className="space-y-6">
      {/* Params */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> {t('Campaign parameters')}
          </CardTitle>
          <CardDescription>
            {t('Declare the URL parameters you pass through ad networks. Each row becomes a macro like {key} available in postbacks and CAPI payloads.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paramsDirty && (
            <div
              role="status"
              className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/40 bg-primary/5 p-3 text-sm"
            >
              <span className="flex items-center gap-2 text-foreground">
                <AlertCircle className="w-4 h-4 text-primary shrink-0" />
                {t('Parameters have unsaved changes — clicks keep using the saved set until you save.')}
              </span>
              <Button size="sm" onClick={save} disabled={saving}>
                {saving ? t('Saving…') : t('Save changes')}
              </Button>
            </div>
          )}
          {/* Header row */}
          <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_7rem_auto] gap-3 text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground mb-2">
            <span>{t('Name')}</span>
            <span className="flex items-center gap-1">
              {t('URL key')}
              <HelpHint text={t('The query-string parameter name in your tracking link — keep it URL-safe: letters, digits and _ (no spaces). Spaces are auto-converted to _ so the link never shows %20.')} />
            </span>
            <span className="flex items-center gap-1">
              {t('Placeholder / default')}
              <HelpHint text={t('Value written into the tracking link for this key: an ad-network macro such as {campaignid}, or a fixed value such as MCLV. A fixed value is also stored on clicks that arrive without the key; a macro in braces is not. Leave empty for keys the ad sets per account, such as tm and tml.')} />
            </span>
            <span className="flex items-center gap-1">
              {t('Sub slot')}
              <HelpHint text={t('Optional: the value is also stored as sub1…sub30 or a named field (external_id, creative_id…). Map the same thing to the same slot in every campaign (e.g. team -> sub8) and one report column and the {sub_id_8} macro cover all buyers; external_id feeds {external_id} in the source postback.')} />
            </span>
            <span />
          </div>
          <div className="space-y-2">
            {params.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('No parameters yet')}</p>
            )}
            {params.map((p, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_7rem_auto] gap-3 items-start">
                <Input
                  aria-label={t('Name')}
                  value={p.name || ''}
                  onChange={(e) => {
                    const next = [...params]; next[i] = { ...p, name: e.target.value }; setParams(next);
                  }}
                />
                <Input
                  aria-label={t('URL key')}
                  value={p.key || ''}
                  onChange={(e) => {
                    const next = [...params]; next[i] = { ...p, key: sanitizeParamKey(e.target.value) }; setParams(next);
                  }}
                />
                <Input
                  aria-label={t('Placeholder / default')}
                  title={isUnfilledMacro(p.placeholder) ? t('Macro: filled by the ad network, not stored on clicks without this key') : undefined}
                  value={p.placeholder || ''}
                  onChange={(e) => {
                    const next = [...params]; next[i] = { ...p, placeholder: e.target.value }; setParams(next);
                  }}
                />
                <Select
                  value={p.slot || 'none'}
                  onValueChange={(v) => {
                    const next = [...params]; next[i] = { ...p, slot: v === 'none' ? '' : v }; setParams(next);
                  }}
                >
                  <SelectTrigger aria-label={t('Sub slot')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">—</SelectItem>
                    {SLOT_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" aria-label={t('Remove parameter')} title={t('Remove parameter')} onClick={() => {
                  setParams(params.filter((_, idx) => idx !== i));
                }}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => setParams([...params, { name: '', key: '', placeholder: '', slot: '' }])}>
              <Plus className="w-4 h-4 mr-1" /> {t('Add parameter')}
            </Button>
            {campaign.traffic_source_id && (
              <Button variant="outline" size="sm" onClick={importFromSource}>
                <Sparkles className="w-4 h-4 mr-1" /> {t('Import from source')}
              </Button>
            )}
          </div>

          {/* Google Ads conversion keys + report keys */}
          <div className="mt-4 space-y-2 rounded-md border border-border bg-surface-2 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">{t('Google Ads conversion keys')}</p>
            <ul className="list-disc space-y-1 pl-4">
              <li>
                <span className="font-mono text-foreground">tml</span>{' '}
                {t('= Submit Lead Form label — set this conversion as Primary. Fired from the visitor’s browser after a successful lead.')}
              </li>
              <li>
                <span className="font-mono text-foreground">tm</span>{' '}
                {t('= Purchase / deposit label — set this conversion as Secondary. Sent by the "Deposit → Purchase" S2S rule; do not add an S2S rule for lead.')}
              </li>
            </ul>
            <p>
              {t('Put both in the Final URL suffix of each Google Ads account (tml=AW-…/lead-label&tm=AW-…/purchase-label) and leave their default empty here.')}
            </p>
            <p>
              {t('Keys are case-sensitive. Any key (a, b, s, c…) is stored on the click, works as a {key} macro and can be picked as a grouping in Reports and Sub-ID reports, next to sub1…sub30. Map it to a sub slot to line it up across campaigns.')}
            </p>
            {badCaseKeys.length > 0 && (
              <p className="flex items-center gap-1 text-destructive">
                <AlertCircle className="w-3 h-3 shrink-0" />
                {t('Use lowercase keys — the tracker does not read:')} <span className="font-mono">{badCaseKeys.join(', ')}</span>
              </p>
            )}
          </div>

          {/* Preview tracking URL */}
          <div className="mt-5 rounded-md border border-border bg-surface-2 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
                <Link2 className="w-3 h-3" /> {t('Tracking URL preview')}
                <HelpHint text={t('The ready-to-copy tracking link for this campaign. Swap the domain for any verified workspace domain to run several domains on one campaign; the ad network fills the placeholders at click time.')} />
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(previewUrl);
                  toast.success(t('Copied'));
                }}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Copy className="w-3 h-3" /> {t('Copy')}
              </button>
            </div>
            <div className="font-mono text-xs break-all text-foreground">{previewUrl}</div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('Paste this as Final URL (Google Ads) or tracking link (Facebook). Replace {key} placeholders with real ad-network macros as needed.')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Keys without a default value (tm, tml, a…) are left out of the link: pass them in the ad, e.g. in the Google Ads Final URL suffix.')}
            </p>
          </div>

          {/* Google Ads gtag toggle — click-time conversion (optimizes on clicks) */}
          <div className="mt-5 flex items-start justify-between gap-3 pt-4 border-t border-border">
            <div>
              <Label className="flex items-center gap-1">
                {t('Fire Google Ads gtag on redirect (click-time conversion)')}
                <HelpHint text={t('Legacy click-time conversion: fires on /r/ before any registration or deposit. Keep this disabled for lead/deposit optimization. Real leads are sent automatically from hosted landings after a successful /api/lead response.')} />
              </Label>
              <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                {t('Keep this OFF for real conversion optimization. Leads fire online from the hosted landing only after /api/lead succeeds; deposits can be forwarded below by S2S on the broker sale postback. Enabling this makes Google optimize on clicks and can double-count conversions.')}
              </p>
            </div>
            <Switch checked={fireGtag} onCheckedChange={setFireGtag} />
          </div>

          {/* Postback IP whitelist */}
          <div className="mt-5 pt-4 border-t border-border space-y-2">
            <Label>{t('Postback IP whitelist')}</Label>
            <Input
              value={ipWhitelist}
              onChange={(e) => setIpWhitelist(e.target.value)}
              placeholder={t('e.g. 185.45.12.1, 91.108.0.0')}
              data-testid="postback-ip-whitelist-input"
            />
            <p className="text-xs text-muted-foreground">
              {t('Comma-separated list of IPs allowed to send postbacks. Leave empty to allow all.')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Incoming postbacks — receive conversions FROM the affiliate network */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowDownToLine className="w-4 h-4" /> {t('Receiving conversions (incoming postback)')}
          </CardTitle>
          <CardDescription>
            {t('Paste these URLs into your affiliate network (e.g. TrackBox) so it notifies this campaign on conversions. Pass your click_id in the network parameter and it comes back here.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Token */}
          <div className="rounded-md border border-border bg-surface-2 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1">
                {t('Postback token')}
                <HelpHint text={t('Secret that authenticates incoming postbacks for this campaign. Paste it as &token=… in the postback URLs you give the network. Keep it private; anyone with it can register conversions.')} />
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowToken(v => !v)}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  {showToken ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showToken ? t('Hide') : t('Show')}
                </button>
                <button
                  onClick={() => copyText(campaign.postback_secret)}
                  disabled={!campaign.postback_secret}
                  className="text-xs text-primary hover:underline flex items-center gap-1 disabled:opacity-40"
                >
                  <Copy className="w-3 h-3" /> {t('Copy')}
                </button>
              </div>
            </div>
            <div className="font-mono text-xs break-all">
              {campaign.postback_secret
                ? (showToken ? campaign.postback_secret : '•'.repeat(24))
                : t('Save the campaign once to generate the token.')}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">{t('Require postback token')}</span>
                <HelpHint text={t("When ON, incoming postbacks must carry this campaign's token, otherwise they are rejected (401). Turn OFF if the network (e.g. TrackBox) sends ONE shared postback for all campaigns: remove &token=… from this URL and conversions are matched by click_id.")} />
              </div>
              <Switch checked={requireToken} onCheckedChange={setRequireToken} />
            </div>
            {!requireToken && (
              <p className="text-[0.7rem] text-muted-foreground mt-2">
                {t('Token check disabled — postbacks are accepted by click_id without a token (remove &token=… from the URL in your network).')}
              </p>
            )}
          </div>

          {/* Network preset */}
          <div className="flex items-center gap-3">
            <Label className="text-xs shrink-0">{t('Network macros')}</Label>
            <Select value={inboundPreset} onValueChange={setInboundPreset}>
              <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(INBOUND_PRESETS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Ready-to-paste URLs */}
          <div className="space-y-2">
            {INBOUND_EVENTS.map(ev => {
              const url = buildInboundUrl(ev);
              return (
                <div key={ev.key} className="rounded-md border border-border bg-surface-2 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold">{t(ev.label)}</span>
                    <button
                      onClick={() => copyText(url)}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" /> {t('Copy')}
                    </button>
                  </div>
                  <div className="font-mono text-[0.7rem] break-all text-muted-foreground">{url}</div>
                </div>
              );
            })}
          </div>

          <div className="flex items-start gap-2 text-[0.7rem] text-muted-foreground">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{t(inbound.hint)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Status mapping — map any network's raw statuses to our buckets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> {t('Status mapping')}
            <HelpHint text={t('Translate the raw status words a network sends into our buckets. Priority: inline URL param > this campaign map > the affiliate-network preset > custom types > built-in defaults.')} />
          </CardTitle>
          <CardDescription>
            {t('Map the network raw statuses to ours (comma-separated). Works for any network and applies to every inbound postback of this campaign. Inline URL params take priority.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t('Lead')} <HelpHint text={t("Raw network statuses to treat as a LEAD (registration). Comma-separated. E.g. new, reg, registration.")} /></Label>
            <Input placeholder="new, reg, registration" value={smLead} onChange={(e) => setSmLead(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">{t('Sale')} <HelpHint text={t("Raw statuses to treat as a SALE/deposit. E.g. ftd, deposit, approved.")} /></Label>
            <Input placeholder="ftd, deposit, approved" value={smSale} onChange={(e) => setSmSale(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">{t('Reject')} <HelpHint text={t("Rejection/bad statuses. E.g. bl, fake, rejected.")} /></Label>
            <Input placeholder="bl, fake, rejected" value={smReject} onChange={(e) => setSmReject(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">{t('Ignore')} <HelpHint text={t("These statuses are silently dropped — not recorded as conversions. E.g. spam, junk, test.")} /></Label>
            <Input placeholder="spam, junk, test" value={smIgnore} onChange={(e) => setSmIgnore(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">{t('Registration')} <HelpHint text={t("Raw statuses to treat as a REGISTRATION (first-class conversion type). E.g. reg, signup.")} /></Label>
            <Input placeholder="reg, signup" value={smRegistration} onChange={(e) => setSmRegistration(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">{t('Deposit')} <HelpHint text={t("Raw statuses to treat as a DEPOSIT (first-class conversion type). E.g. dep, ftd, deposit.")} /></Label>
            <Input placeholder="dep, ftd, deposit" value={smDeposit} onChange={(e) => setSmDeposit(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">{t('Trash')} <HelpHint text={t("Raw statuses to treat as TRASH (recorded but excluded from payouts). E.g. trash, invalid.")} /></Label>
            <Input placeholder="trash, invalid" value={smTrash} onChange={(e) => setSmTrash(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {/* S2S Postbacks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Beaker className="w-4 h-4" /> {t('Forwarding — S2S postbacks')}
            <HelpHint text={t('Outbound calls that notify another system (your traffic source, another tracker, Telegram) when a conversion arrives. For ad-platform CAPI (Facebook/Google/TikTok) use Conversion forwarders instead — hashed PII and retries.')} />
          </CardTitle>
          <CardDescription>
            {t('Outbound only: these HTTP calls notify another system after XPNSR records a conversion. Affiliate-network presets and inbound URLs belong on the Affiliate networks page and must be pasted into the partner dashboard — they are not forwarding rules.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {postbacks.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('No postbacks yet')}</p>
          )}
          {postbacks.map((pb, i) => (
            <div key={i} className="rounded-md border border-border p-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-start">
                <div>
                  <Label className="text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">URL</Label>
                  <Input
                    placeholder="https://example.com/pb?cid={click_id}&sum={payout}&status={status}"
                    value={pb.url || ''}
                    onChange={(e) => {
                      const next = [...postbacks]; next[i] = { ...pb, url: e.target.value }; setPostbacks(next);
                    }}
                  />
                </div>
                <div>
                  <Label className="text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">{t('Method')}</Label>
                  <Select value={pb.method || 'GET'} onValueChange={(v) => {
                    const next = [...postbacks]; next[i] = { ...pb, method: v }; setPostbacks(next);
                  }}>
                    <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2 pt-5">
                  <Button variant="outline" size="sm" onClick={() => testOne(pb)}>
                    <Beaker className="w-4 h-4 mr-1" /> {t('Test')}
                  </Button>
                  <Button variant="outline" size="icon" aria-label={t('Remove postback')} title={t('Remove postback')} onClick={() => {
                    setPostbacks(postbacks.filter((_, idx) => idx !== i));
                  }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">{t('Statuses')} <HelpHint text={t('Sale also fires for deposits (ftd, dep, deposit) and Lead also for registrations (reg, signup), whatever the broker calls them.')} /></Label>
                <div className="flex flex-wrap gap-3 mt-1">
                  {Object.entries(STATUS_LABELS).map(([k, label]) => {
                    const selected = (pb.statuses || []).includes(k);
                    return (
                      <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const cur = new Set(pb.statuses || []);
                            if (e.target.checked) cur.add(k); else cur.delete(k);
                            const next = [...postbacks];
                            next[i] = { ...pb, statuses: Array.from(cur) };
                            setPostbacks(next);
                          }}
                        />
                        {t(label)}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="outline" size="sm" onClick={() => setPostbacks([
              ...postbacks,
              { url: '', method: 'GET', statuses: ['lead'], is_active: true },
            ])}>
              <Plus className="w-4 h-4 mr-1" /> {t('Add postback')}
            </Button>

            <Button variant="outline" size="sm" onClick={() => setTgOpen(true)}>
              <Send className="w-4 h-4 mr-1" /> Telegram
            </Button>

            <Button variant="outline" size="sm" onClick={() => setGaOpen(true)}>
              <Send className="w-4 h-4 mr-1" /> Google Ads
            </Button>

            {sourcePostback && (
              <Button variant="outline" size="sm" onClick={addSourcePostback} title={sourcePostback}>
                <Link2 className="w-4 h-4 mr-1" /> {t('Source postback')}
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={loadEvents}>
              {t('Show recent log')}
            </Button>
          </div>

          {/* Telegram quick-setup — adds Lead & Deposit postbacks in one step */}
          <Dialog open={tgOpen} onOpenChange={setTgOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('Telegram notifications')}</DialogTitle>
                <DialogDescription>
                  {t('Enter the bot token and chat_id — Lead and Deposit postbacks with a ready-made message (Campaign, Sub1..4, Source, Payout) will be added. Then press Save.')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">{t('Bot token')}</Label>
                  <Input placeholder="7288898695:AAF..." value={tgBot} onChange={(e) => setTgBot(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">{t('Chat ID')}</Label>
                  <Input placeholder="-1002845945063" value={tgChat} onChange={(e) => setTgChat(e.target.value)} />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={tgLead} onChange={(e) => setTgLead(e.target.checked)} /> {t('Lead')}
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={tgDep} onChange={(e) => setTgDep(e.target.checked)} /> {t('Deposit')}
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setTgOpen(false)}>{t('Cancel')}</Button>
                <Button size="sm" onClick={addTelegram}>{t('Add')}</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Google Ads quick-setup — server-side conversion beacon on the postback */}
          <Dialog open={gaOpen} onOpenChange={setGaOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('Google Ads conversions')}</DialogTitle>
                <DialogDescription>
                  {t('Adds only the deposit/Purchase S2S postback. It fires on the broker sale postback and matches the Google click by {gclid}. The account id and deposit label come from the per-click {tm} tag (AW-<account>/<deposit-label>) in the ad Final URL suffix.')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={gaDep} onChange={(e) => setGaDep(e.target.checked)} />
                  {t('Deposit → Purchase (with payout value) — fires on sale')}
                </label>
                <p className="text-xs text-muted-foreground">
                  {t('Lead S2S is intentionally not added: the hosted landing already sends the Lead conversion online via client-side gtag after the CRM accepts /api/lead. Adding a lead S2S rule would risk double-counting the same lead.')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('Tracking params (campaignid, adgroupid, keyword, tm, tml, sub1–3) are declared once on the Google Ads traffic source — use "Import from source" to add them to a campaign.')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('For online leads, pass tml=AW-<account>/<lead-label> in each ad account Final URL suffix. Keep "Fire Google Ads gtag on redirect" OFF: the tracker injects a separate lead gtag into hosted landings and fires it only after a successful lead submission. Configure Lead as Primary when bidding on leads; keep Purchase secondary until deposit reporting is verified, then adjust goals to your bidding strategy.')}
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setGaOpen(false)}>{t('Cancel')}</Button>
                <Button size="sm" onClick={addGoogleAds}>{t('Add')}</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Macro cheat-sheet */}
          <div className="rounded-md border border-border bg-surface-2 p-3">
            <div className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground mb-2">
              {t('Available macros')}
            </div>
            <div className="flex flex-wrap gap-2">
              {knownMacros.map(m => (
                <Badge key={m} variant="outline" className="font-mono">{`{${m}}`}</Badge>
              ))}
            </div>
          </div>

          {showEvents && (
            <div className="rounded-md border border-border bg-surface-2 p-3 max-h-72 overflow-y-auto space-y-1">
              {events.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('No events yet')}</p>
              ) : events.map(ev => (
                <div key={ev.id} className="flex items-start gap-2 text-xs font-mono">
                  {ev.ok
                    ? <CheckCircle2 className="w-3 h-3 text-primary mt-0.5 shrink-0" />
                    : <AlertCircle className="w-3 h-3 text-destructive mt-0.5 shrink-0" />}
                  <span className="text-muted-foreground shrink-0">
                    {new Date(ev.created_at).toLocaleTimeString()}
                  </span>
                  <span className="shrink-0">{ev.method} {ev.http_status ?? ''}</span>
                  <span className="truncate">{ev.error || ev.url}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving}>
          {saving ? t('Saving…') : t('Save changes')}
        </Button>
      </div>
    </div>
  );
}
